
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfMonth, subMonths, format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { useEffect } from "react";

interface TransactionData {
  data: { date: string; amount: number }[];
  totalRevenue: number;
  averageDaily: number;
  transactionCount: number;
}

export const useRevenueData = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel('transactions-changes')
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'poultry_transactions'
        },
        () => {
          console.log('Transaction modifiée, invalidation du cache');
          queryClient.invalidateQueries({ queryKey: ['transactions_summary'] });
          queryClient.invalidateQueries({ queryKey: ['validatedAmounts'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const fetchTransactionData = async (startDate: Date): Promise<TransactionData> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Non authentifié");

    // 1. Récupérer toutes les transactions pour le calcul des totaux journaliers
    const { data: transactions, error } = await supabase
      .from("poultry_transactions")
      .select("transaction_type, total_amount, refund_amount, created_at")
      .eq('created_by', user.id)
      .eq('is_deducted_from_revenue', true)
      .gte("created_at", startDate.toISOString());

    if (error) throw error;

    // 2. Calculer les totaux journaliers en tenant compte des ventes et des retours
    const dailyTotals = transactions.reduce((acc: { [key: string]: number }, transaction: any) => {
      const date = format(parseISO(transaction.created_at), 'dd/MM', { locale: fr });
      const amount = transaction.transaction_type === 'sale' ? 
        transaction.total_amount : 
        (transaction.transaction_type === 'return' ? -transaction.refund_amount : 0);
      
      acc[date] = (acc[date] || 0) + amount;
      return acc;
    }, {});

    const data = Object.entries(dailyTotals).map(([date, amount]) => ({
      date,
      amount,
    })).sort((a, b) => {
      const [dayA, monthA] = a.date.split('/');
      const [dayB, monthB] = b.date.split('/');
      return (parseInt(monthA) - parseInt(monthB)) || (parseInt(dayA) - parseInt(dayB));
    });

    // 3. Calculer le total des ventes uniquement
    const totalRevenue = transactions.reduce((sum, transaction) => {
      if (transaction.transaction_type === 'sale') {
        return sum + transaction.total_amount;
      }
      return sum;
    }, 0);

    const salesTransactions = transactions.filter(t => t.transaction_type === 'sale');
    const averageDaily = salesTransactions.length > 0 ? totalRevenue / salesTransactions.length : 0;

    return {
      data,
      totalRevenue,
      averageDaily,
      transactionCount: salesTransactions.length,
    };
  };

  const { data: currentMonthData } = useQuery({
    queryKey: ["transactions_summary", "current"],
    queryFn: async () => {
      const startDate = startOfMonth(new Date());
      return fetchTransactionData(startDate);
    },
  });

  const { data: previousMonthData } = useQuery({
    queryKey: ["transactions_summary", "previous"],
    queryFn: async () => {
      const startDate = startOfMonth(subMonths(new Date(), 1));
      return fetchTransactionData(startDate);
    },
  });

  const { data: validatedAmount } = useQuery({
    queryKey: ["validatedAmounts", "month"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const startDate = startOfMonth(new Date());
      
      // Récupérer uniquement les ventes validées pour le mois en cours
      const { data: transactions, error } = await supabase
        .from("poultry_transactions")
        .select("total_amount")
        .eq("created_by", user.id)
        .eq("is_deducted_from_revenue", true)
        .eq("transaction_type", "sale")
        .gte("created_at", startDate.toISOString());

      if (error) throw error;

      // Calculer le total des ventes validées
      return transactions.reduce((sum, transaction) => sum + transaction.total_amount, 0);
    },
  });

  return {
    currentMonthData,
    previousMonthData,
    validatedAmount,
  };
};
